import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { env } from '@/config/env';
import {
  useGetGeoFloodJobsQuery,
  useLazyGetGeoFloodResultByIdQuery,
  useRetryGeoFloodJobMutation,
} from '@/features/analytics/api/geoProcessingAnalysisApi';
import { hydrateSarSceneGalleryAlphaEarth } from '@/features/analytics/utils/sarResultAlphaEarthGallery';
import { useAuth } from '@/features/auth/hooks/useAuth';
import type { FloodJob } from '@/mocks/floodJobs';
import { shouldFetchOperationalApi } from '@/utils/dataSource';

function cloneInitialJobs(): FloodJob[] {
  return [];
}

export interface FloodJobsContextValue {
  jobs: FloodJob[];
  addJob: (job: FloodJob) => void;
  /** Cập nhật một job đã có (ví dụ: sau khi API trả về kết quả). */
  updateJob: (id: string, updates: Partial<FloodJob>) => void;
  /** Chỉ job `failed` — đưa về chạy lại từ đầu (mock pipeline). */
  rerunFloodJob: (jobId: string) => void;
  /** Resolved from `jobs` so the modal stays in sync after completion updates. */
  resultModalJob: FloodJob | null;
  presentResultModal: (job: FloodJob | null) => void;
  dismissResultModal: () => void;
  /** Map-submitted SAR job id — drives map progress overlay. */
  activeSarJobId: string | null;
  setActiveSarJobId: (id: string | null) => void;
  replaceJobId: (oldId: string, newId: string, updates?: Partial<FloodJob>) => void;
  /** Tải lại dữ liệu chi tiết (Metadata/Geometry) của một kết quả cũ và hiển thị modal. */
  viewHistoryResult: (jobId: string) => Promise<void>;
}

const FloodJobsContext = createContext<FloodJobsContextValue | null>(null);

export function FloodJobsProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const skipJobsApi = !shouldFetchOperationalApi() || !isAuthenticated;
  const [jobs, setJobs] = useState<FloodJob[]>(cloneInitialJobs);
  const [resultModalJobId, setResultModalJobId] = useState<string | null>(null);
  const [activeSarJobId, setActiveSarJobId] = useState<string | null>(null);

  const [fetchResult] = useLazyGetGeoFloodResultByIdQuery();
  const [triggerRetry] = useRetryGeoFloodJobMutation();

  const hasActiveJobs = useMemo(
    () => jobs.some((j) => j.status === 'running' || j.status === 'queued'),
    [jobs],
  );

  const { data: apiJobs, isSuccess: floodJobsApiReady } = useGetGeoFloodJobsQuery(undefined, {
    skip: skipJobsApi,
    pollingInterval: skipJobsApi ? 0 : hasActiveJobs ? 5000 : 60000,
  });

  const clientTrackedJobIdsRef = useRef(new Set<string>());

  const resultModalJob = useMemo(
    () => (resultModalJobId ? (jobs.find((j) => j.id === resultModalJobId) ?? null) : null),
    [jobs, resultModalJobId],
  );

  const jobsRef = useRef(jobs);
  useEffect(() => {
    jobsRef.current = jobs;
  }, [jobs]);

  useEffect(() => {
    if (skipJobsApi || !floodJobsApiReady || !apiJobs) return;
    const mapped = apiJobs;
    const apiIds = new Set(mapped.map((j) => j.id));
    setJobs((prev) => {
      const locals = prev.filter(
        (j) => clientTrackedJobIdsRef.current.has(j.id) && !apiIds.has(j.id),
      );
      const byId = new Map<string, FloodJob>();
      for (const j of locals) byId.set(j.id, j);
      for (const j of mapped) byId.set(j.id, j);
      return [...byId.values()].sort(
        (a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime(),
      );
    });
  }, [skipJobsApi, floodJobsApiReady, apiJobs]);

  const prevStatusRef = useRef<Map<string, FloodJob['status']>>(new Map());
  const seededRef = useRef(false);
  const modalHydrateGenRef = useRef(0);

  const addJob = useCallback((job: FloodJob) => {
    clientTrackedJobIdsRef.current.add(job.id);
    setJobs((prev) => [job, ...prev]);
  }, []);

  const updateJob = useCallback((id: string, updates: Partial<FloodJob>) => {
    setJobs((prev) => prev.map((j) => (j.id === id ? { ...j, ...updates } : j)));
  }, []);

  const replaceJobId = useCallback((oldId: string, newId: string, updates?: Partial<FloodJob>) => {
    const oldStatus = prevStatusRef.current.get(oldId);
    if (oldStatus) {
      prevStatusRef.current.set(newId, oldStatus);
      prevStatusRef.current.delete(oldId);
    }
    clientTrackedJobIdsRef.current.delete(oldId);
    clientTrackedJobIdsRef.current.add(newId);
    setJobs((prev) => prev.map((j) => (j.id === oldId ? { ...j, ...updates, id: newId } : j)));
    setActiveSarJobId((current) => (current === oldId ? newId : current));
  }, []);

  const rerunFloodJob = useCallback(
    async (jobId: string) => {
      try {
        await triggerRetry(jobId).unwrap();
        setResultModalJobId((id) => (id === jobId ? null : id));
        setActiveSarJobId(jobId);
      } catch (err) {
        console.error('Failed to rerun job:', err);
      }
    },
    [triggerRetry],
  );

  const presentResultModal = useCallback((job: FloodJob | null) => {
    setResultModalJobId(job?.id ?? null);
  }, []);

  const dismissResultModal = useCallback(() => {
    setResultModalJobId(null);
  }, []);

  const viewHistoryResult = useCallback(
    async (jobId: string) => {
      try {
        const job = jobsRef.current.find((j) => j.id === jobId);

        // Nếu đã có kết quả chi tiết (geometry hoặc gallery) thì không cần fetch lại
        if (job?.results?.floodExtentGeometry || job?.results?.sceneGallery) {
          setResultModalJobId(jobId);
          return;
        }

        let targetId = job?.resultId || jobId;
        let fullJob;

        try {
          fullJob = await fetchResult(targetId).unwrap();
        } catch (err) {
          // Nếu dùng resultId lỗi (404), thử dùng jobId gốc làm fallback
          if (targetId !== jobId) {
            targetId = jobId;
            fullJob = await fetchResult(targetId).unwrap();
          } else {
            throw err;
          }
        }

        if (fullJob) {
          setJobs((prev) =>
            prev.map((j) => (j.id === jobId ? { ...j, ...fullJob, id: jobId } : j)),
          );
          setResultModalJobId(jobId);
        }
      } catch (err) {
        console.error('Failed to load history result:', err);
      }
    },
    [fetchResult],
  );

  useEffect(() => {
    if (!seededRef.current) {
      seededRef.current = true;
      for (const j of jobs) prevStatusRef.current.set(j.id, j.status);
      return;
    }
    for (const j of jobs) {
      const prev = prevStatusRef.current.get(j.id);
      const isTerminal = j.status === 'completed' || j.status === 'failed';

      if (isTerminal && j.id === activeSarJobId) {
        queueMicrotask(() => {
          setActiveSarJobId(null);
        });
        if (j.status === 'completed' && prev !== 'completed') {
          queueMicrotask(() => {
            void viewHistoryResult(j.id);
          });
        }
      }
      prevStatusRef.current.set(j.id, j.status);
    }
  }, [jobs, activeSarJobId, viewHistoryResult]);

  useEffect(() => {
    if (!resultModalJobId) return;
    if (!env.SAR_RESULT_ALPHAEARTH_PREVIEW) return;

    const job = jobsRef.current.find((j) => j.id === resultModalJobId);
    if (!job) return;
    if (job.status !== 'completed' || !job.results?.sceneGallery?.length) return;
    if (!job.aoiPolygon) return;
    if (job.results.sceneGalleryHydrateDone) return;
    if (job.results.sceneGalleryLoading) return;

    const jobId = job.id;
    const slides = job.results.sceneGallery.map((s) => ({ ...s }));
    const runId = ++modalHydrateGenRef.current;
    let cancelled = false;

    queueMicrotask(() => {
      if (cancelled) return;
      setJobs((prev) =>
        prev.map((j) =>
          j.id === jobId && j.results
            ? { ...j, results: { ...j.results, sceneGalleryLoading: true } }
            : j,
        ),
      );
    });

    void hydrateSarSceneGalleryAlphaEarth(job, slides)
      .then((gallery) => {
        if (cancelled || runId !== modalHydrateGenRef.current) return;
        setJobs((prev) =>
          prev.map((j) =>
            j.id === jobId && j.status === 'completed' && j.results
              ? {
                  ...j,
                  results: {
                    ...j.results,
                    sceneGallery: gallery,
                    sceneGalleryLoading: false,
                    sceneGalleryHydrateDone: true,
                  },
                }
              : j,
          ),
        );
      })
      .catch(() => {
        if (cancelled || runId !== modalHydrateGenRef.current) return;
        setJobs((prev) =>
          prev.map((j) =>
            j.id === jobId && j.status === 'completed' && j.results
              ? {
                  ...j,
                  results: {
                    ...j.results,
                    sceneGalleryLoading: false,
                    sceneGalleryHydrateDone: true,
                  },
                }
              : j,
          ),
        );
      });

    return () => {
      cancelled = true;
      modalHydrateGenRef.current += 1;
      queueMicrotask(() => {
        setJobs((prev) =>
          prev.map((j) =>
            j.id === jobId && j.results?.sceneGalleryLoading && !j.results.sceneGalleryHydrateDone
              ? { ...j, results: { ...j.results, sceneGalleryLoading: false } }
              : j,
          ),
        );
      });
    };
  }, [resultModalJobId]);

  const value = useMemo(
    () => ({
      jobs,
      addJob,
      updateJob,
      rerunFloodJob,
      resultModalJob,
      presentResultModal,
      dismissResultModal,
      activeSarJobId,
      setActiveSarJobId,
      replaceJobId,
      viewHistoryResult,
    }),
    [
      jobs,
      resultModalJob,
      addJob,
      updateJob,
      rerunFloodJob,
      presentResultModal,
      dismissResultModal,
      activeSarJobId,
      replaceJobId,
      viewHistoryResult,
    ],
  );

  return <FloodJobsContext.Provider value={value}>{children}</FloodJobsContext.Provider>;
}

export function useFloodJobs(): FloodJobsContextValue {
  const ctx = useContext(FloodJobsContext);
  if (!ctx) {
    throw new Error('useFloodJobs must be used within FloodJobsProvider');
  }
  return ctx;
}
